import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Package, Search, RefreshCw, Truck, CheckCircle, AlertCircle, 
  Box, MapPin, Clock, Filter, ChevronDown, ChevronUp 
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status configuration with colors and icons
const STATUS_CONFIG = {
  'created': { label: 'Creado', color: 'bg-gray-100 text-gray-700', icon: Package },
  'received': { label: 'Recibido', color: 'bg-blue-100 text-blue-700', icon: Box },
  'in_warehouse': { label: 'En Bodega', color: 'bg-yellow-100 text-yellow-700', icon: MapPin },
  'in_transit': { label: 'En Tránsito', color: 'bg-purple-100 text-purple-700', icon: Truck },
  'customs': { label: 'En Aduana', color: 'bg-orange-100 text-orange-700', icon: Clock },
  'out_for_delivery': { label: 'En Reparto', color: 'bg-indigo-100 text-indigo-700', icon: Truck },
  'delivered': { label: 'Entregado', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  'returned': { label: 'Devuelto', color: 'bg-red-100 text-red-700', icon: AlertCircle }
};

const ManagePackages = ({ token, clients = [] }) => {
  const [trackings, setTrackings] = useState([]);
  const [filteredTrackings, setFilteredTrackings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTrackings, setSelectedTrackings] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkLocation, setBulkLocation] = useState('');
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState(null);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  useEffect(() => {
    fetchTrackings();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [trackings, searchTerm, statusFilter]);

  const fetchTrackings = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/trackings`, axiosConfig);
      setTrackings(response.data);
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al cargar los paquetes' });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...trackings];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(t =>
        t.tracking_number?.toLowerCase().includes(search) ||
        t.client_name?.toLowerCase().includes(search) ||
        t.description?.toLowerCase().includes(search)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter);
    }

    setFilteredTrackings(filtered);
  };

  const toggleSelectAll = () => {
    if (selectedTrackings.length === filteredTrackings.length) {
      setSelectedTrackings([]);
    } else {
      setSelectedTrackings(filteredTrackings.map(t => t.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedTrackings(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const updateSingleStatus = async (trackingId, newStatus) => {
    try {
      await axios.put(`${API}/admin/trackings/${trackingId}`, 
        { status: newStatus },
        axiosConfig
      );
      await fetchTrackings();
      setMessage({ type: 'success', text: 'Estado actualizado correctamente' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al actualizar el estado' });
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedTrackings.length === 0) {
      setMessage({ type: 'error', text: 'Selecciona al menos un paquete' });
      return;
    }

    if (!bulkStatus && !bulkLocation) {
      setMessage({ type: 'error', text: 'Selecciona un estado o ubicación para actualizar' });
      return;
    }

    setUpdating(true);
    setMessage(null);

    try {
      const updateData = {};
      if (bulkStatus) updateData.status = bulkStatus;
      if (bulkLocation) updateData.location = bulkLocation;

      // Update all selected trackings
      await Promise.all(
        selectedTrackings.map(id =>
          axios.put(`${API}/admin/trackings/${id}`, updateData, axiosConfig)
        )
      );

      await fetchTrackings();
      setSelectedTrackings([]);
      setBulkStatus('');
      setBulkLocation('');
      setMessage({ 
        type: 'success', 
        text: `✅ Se actualizaron ${selectedTrackings.length} paquete(s) correctamente` 
      });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al actualizar los paquetes' });
    } finally {
      setUpdating(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG['created'];
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon size={12} />
        {config.label}
      </span>
    );
  };

  const getClientName = (tracking) => {
    if (tracking.client_name) return tracking.client_name;
    const client = clients.find(c => c.id === tracking.client_id);
    return client?.full_name || 'Sin asignar';
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            <RefreshCw className="text-blue-600" />
            Gestionar Paquetes
          </CardTitle>
          <CardDescription>
            Actualiza estados y ubicaciones de paquetes de forma individual o masiva
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          
          {/* Message */}
          {message && (
            <div className={`p-3 rounded-lg flex items-center gap-2 ${
              message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
            }`}>
              {message.type === 'success' ? <CheckCircle size={18} /> : <AlertCircle size={18} />}
              {message.text}
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-3 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Input
                placeholder="Buscar por tracking, cliente, descripción..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
              <Search className="absolute left-3 top-2.5 text-gray-400" size={18} />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Filtrar por estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={fetchTrackings} className="gap-2">
              <RefreshCw size={16} />
              Actualizar
            </Button>
          </div>

          {/* Bulk Actions */}
          {selectedTrackings.length > 0 && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <div className="flex flex-wrap items-center gap-4">
                <span className="font-semibold text-blue-800">
                  {selectedTrackings.length} paquete(s) seleccionado(s)
                </span>
                
                <Select value={bulkStatus} onValueChange={setBulkStatus}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Cambiar estado a..." />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={bulkLocation} onValueChange={setBulkLocation}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Cambiar ubicación..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="miami_warehouse">Bodega Miami</SelectItem>
                    <SelectItem value="in_transit">En Tránsito</SelectItem>
                    <SelectItem value="customs">En Aduana</SelectItem>
                    <SelectItem value="venezuela_warehouse">Bodega Venezuela</SelectItem>
                    <SelectItem value="delivered">Entregado</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button 
                  onClick={handleBulkUpdate} 
                  disabled={updating}
                  className="gap-2"
                >
                  {updating ? (
                    <>
                      <RefreshCw size={16} className="animate-spin" />
                      Actualizando...
                    </>
                  ) : (
                    <>
                      <CheckCircle size={16} />
                      Aplicar Cambios
                    </>
                  )}
                </Button>
                
                <Button 
                  variant="ghost" 
                  onClick={() => setSelectedTrackings([])}
                >
                  Cancelar
                </Button>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-3 text-left">
                      <Checkbox
                        checked={selectedTrackings.length === filteredTrackings.length && filteredTrackings.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </th>
                    <th className="px-3 py-3 text-left font-medium text-gray-600">Tracking</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-600">Cliente</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-600">Descripción</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-600">Carrier</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-600">Peso</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-600">Estado</th>
                    <th className="px-3 py-3 text-left font-medium text-gray-600">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="text-center py-8 text-gray-500">
                        <RefreshCw className="animate-spin mx-auto mb-2" size={24} />
                        Cargando paquetes...
                      </td>
                    </tr>
                  ) : filteredTrackings.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-8 text-gray-500">
                        <Package className="mx-auto mb-2 text-gray-400" size={32} />
                        No se encontraron paquetes
                      </td>
                    </tr>
                  ) : (
                    filteredTrackings.map((tracking) => (
                      <tr key={tracking.id} className="hover:bg-gray-50">
                        <td className="px-3 py-3">
                          <Checkbox
                            checked={selectedTrackings.includes(tracking.id)}
                            onCheckedChange={() => toggleSelect(tracking.id)}
                          />
                        </td>
                        <td className="px-3 py-3 font-mono text-xs">
                          {tracking.tracking_number}
                        </td>
                        <td className="px-3 py-3">
                          <div>
                            <p className="font-medium">{getClientName(tracking)}</p>
                            <p className="text-xs text-gray-500">{tracking.client_locker || ''}</p>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-gray-600 max-w-[150px] truncate">
                          {tracking.description || '-'}
                        </td>
                        <td className="px-3 py-3">
                          <span className="bg-gray-100 px-2 py-1 rounded text-xs">
                            {tracking.carrier || '-'}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          {tracking.weight ? `${tracking.weight} lb` : '-'}
                        </td>
                        <td className="px-3 py-3">
                          {getStatusBadge(tracking.status)}
                        </td>
                        <td className="px-3 py-3">
                          <Select 
                            value={tracking.status || 'created'} 
                            onValueChange={(value) => updateSingleStatus(tracking.id, value)}
                          >
                            <SelectTrigger className="h-8 text-xs w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                                <SelectItem key={key} value={key}>{config.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {/* Footer */}
            <div className="bg-gray-50 px-4 py-3 border-t flex justify-between items-center">
              <span className="text-sm text-gray-600">
                Mostrando {filteredTrackings.length} de {trackings.length} paquetes
              </span>
              <div className="flex gap-4 text-sm">
                {Object.entries(STATUS_CONFIG).slice(0, 4).map(([key, config]) => {
                  const count = trackings.filter(t => t.status === key).length;
                  return count > 0 ? (
                    <span key={key} className={`${config.color} px-2 py-1 rounded`}>
                      {config.label}: {count}
                    </span>
                  ) : null;
                })}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ManagePackages;
